/**
 * Centralized query keys factory for React Query
 * Provides type-safe and hierarchical query keys for cache management
 */

export const queryKeys = {
  // Content keys
  content: {
    all: () => ['content'] as const,
    lists: () => [...queryKeys.content.all(), 'list'] as const,
    list: () => [...queryKeys.content.lists()] as const,
    details: () => [...queryKeys.content.all(), 'detail'] as const,
    detail: (key: string) => [...queryKeys.content.details(), key] as const,
  },

  // Product keys
  products: {
    all: () => ['products'] as const,
    lists: () => [...queryKeys.products.all(), 'list'] as const,
    list: (filters?: { featured?: boolean }) => [...queryKeys.products.lists(), filters] as const,
    details: () => [...queryKeys.products.all(), 'detail'] as const,
    detail: (id: string) => [...queryKeys.products.details(), id] as const,
  },

  // Service keys
  services: {
    all: () => ['services'] as const,
    lists: () => [...queryKeys.services.all(), 'list'] as const,
    list: (filters?: { showPublic?: boolean }) => [...queryKeys.services.lists(), filters] as const,
    details: () => [...queryKeys.services.all(), 'detail'] as const,
    detail: (id: string) => [...queryKeys.services.details(), id] as const,
  },

  // Meeting keys
  meetings: {
    all: () => ['meetings'] as const,
    lists: () => [...queryKeys.meetings.all(), 'list'] as const,
    list: (filters?: {
      fromToday?: boolean;
      occupiedSlots?: boolean;
      dateRange?: { start: string; end: string };
    }) => [...queryKeys.meetings.lists(), filters] as const,
    details: () => [...queryKeys.meetings.all(), 'detail'] as const,
    detail: (id: string) => [...queryKeys.meetings.details(), id] as const,
  },

  // FAQ keys
  faqs: {
    all: () => ['faqs'] as const,
    lists: () => [...queryKeys.faqs.all(), 'list'] as const,
    list: (filters?: { type?: 'piercing' | 'tattoo' }) =>
      [...queryKeys.faqs.lists(), filters] as const,
    details: () => [...queryKeys.faqs.all(), 'detail'] as const,
    detail: (id: string) => [...queryKeys.faqs.details(), id] as const,
  },

  // Flash keys
  flashes: {
    all: () => ['flashes'] as const,
    lists: () => [...queryKeys.flashes.all(), 'list'] as const,
    list: (filters?: { active?: boolean }) => [...queryKeys.flashes.lists(), filters] as const,
    details: () => [...queryKeys.flashes.all(), 'detail'] as const,
    detail: (id: string) => [...queryKeys.flashes.details(), id] as const,
  },

  // Hero keys
  heroes: {
    all: () => ['heroes'] as const,
    lists: () => [...queryKeys.heroes.all(), 'list'] as const,
    list: () => [...queryKeys.heroes.lists()] as const,
    details: () => [...queryKeys.heroes.all(), 'detail'] as const,
    detail: (id: string) => [...queryKeys.heroes.details(), id] as const,
    byType: (type: 'piercing' | 'tattoo') => [...queryKeys.heroes.all(), 'type', type] as const,
    byId: (id: string) => [...queryKeys.heroes.details(), id] as const,
  },

  // Tattoo image keys
  tattooImages: {
    all: () => ['tattoo-images'] as const,
    lists: () => [...queryKeys.tattooImages.all(), 'list'] as const,
    list: () => [...queryKeys.tattooImages.lists()] as const,
    details: () => [...queryKeys.tattooImages.all(), 'detail'] as const,
    detail: (id: string) => [...queryKeys.tattooImages.details(), id] as const,
  },

  // Project keys
  projects: {
    all: () => ['projects'] as const,
    lists: () => [...queryKeys.projects.all(), 'list'] as const,
    list: () => [...queryKeys.projects.lists()] as const,
    details: () => [...queryKeys.projects.all(), 'detail'] as const,
    detail: (id: string) => [...queryKeys.projects.details(), id] as const,
  },

  // Client keys
  clients: {
    all: () => ['clients'] as const,
    lists: () => [...queryKeys.clients.all(), 'list'] as const,
    list: () => [...queryKeys.clients.lists()] as const,
    details: () => [...queryKeys.clients.all(), 'detail'] as const,
    detail: (id: string) => [...queryKeys.clients.details(), id] as const,
  },
} as const;
